/**
 * Phase-3 confidence aggregation + traffic-light buckets.
 *
 * Thresholds default to 0.60 (red<) / 0.85 (yellow<) / ≥0.85 green.
 * Aggregation: average of per-cell confidences across the card, or NaN if
 * no cells are present (e.g. manual-entry mode).
 */
export type Bucket = "green" | "yellow" | "red" | "none";

export const THRESHOLDS = {
  green: 0.85,
  yellow: 0.60,
};

export function bucketOf(score: number | null | undefined): Bucket {
  if (score == null || Number.isNaN(score)) return "none";
  if (score >= THRESHOLDS.green) return "green";
  if (score >= THRESHOLDS.yellow) return "yellow";
  return "red";
}

export function aggregateTimesheetConfidence(data: any): number | null {
  const days = data?.days;
  if (!days || typeof days !== "object") return null;
  const scores: number[] = [];
  for (const day of Object.values(days) as any[]) {
    if (!day || typeof day !== "object") continue;
    for (const field of ["von1", "bis1", "von2", "bis2", "symb"]) {
      const v = day[field];
      if (v && typeof v === "object" && typeof v.confidence === "number") {
        scores.push(v.confidence);
      }
    }
  }
  if (scores.length === 0) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

export function aggregateExpenseConfidence(data: any): number | null {
  const entries = data?.entries;
  if (!Array.isArray(entries)) return null;
  const scores: number[] = [];
  for (const entry of entries) {
    if (!entry) continue;
    for (const field of ["datum", "kz_nr", "kunde", "ort", "abfahrt", "rueckkehr"]) {
      const v = entry[field];
      if (v && typeof v === "object" && typeof v.confidence === "number") {
        scores.push(v.confidence);
      }
    }
  }
  if (scores.length === 0) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

export function aggregateConfidence(kind: string, data: any): number | null {
  if (kind === "timesheet") return aggregateTimesheetConfidence(data);
  if (kind === "expense") return aggregateExpenseConfidence(data);
  return null;
}

/** Max tier across all Phase-7 tier-resolvers stored in data.tier_path.
 *  Returns null if no tier data present. */
export function cardMaxTier(data: any): 1 | 2 | 3 | 4 | null {
  const tp = data?.tier_path;
  if (!tp || typeof tp !== "object") return null;
  const tiers: number[] = [];
  for (const key of ["rotation", "classification", "assignment", "ocr_quality"]) {
    const r = tp[key];
    if (r && typeof r === "object" && typeof r.tier === "number") {
      tiers.push(r.tier);
    }
  }
  if (tiers.length === 0) return null;
  const max = Math.max(...tiers);
  return (max >= 1 && max <= 4 ? max : null) as 1 | 2 | 3 | 4 | null;
}
